import React from "react"
import {
  type EntityCategory,
  type EntityTransaction,
  ProviderType,
} from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import { isTransaction } from "src/frontend/components/Shared/Transactions/types"
import { useAllowTransactionsTableEdits } from "src/frontend/components/Shared/Transactions/Updates/useAllowTransactionsTableEdits"
import { useUserSettings } from "src/frontend/hooks/useUserSettings"
import { CategoryHover } from "src/shared/components/CategoryHover/CategoryHover"
import { CategoryTypeahead } from "src/shared/components/Typeahead/CategoryTypeahead"

const StyledCategoryTypeahead = styled(CategoryTypeahead)`
  --category-typeahead-text-muted-color: ${colors.secondary};
  --category-typeahead-text-color: ${colors.secondary};
  --category-typeahead-text-hover-color: ${colors.secondary};
  --category-typeahead-border-color: ${colors.secondary20};
  --category-typeahead-border-hover-color: ${colors.secondary50};
  --category-typeahead-background-color: ${colors.transparent};
  --category-typeahead-background-hover-color: ${colors.white};
  --category-typeahead-background-disabled-color: ${colors.transparent};
  --category-typeahead-input-v-padding: 4px;

  .categoryDetails {
    width: 100%;
  }

  .categoryIcon {
    ${svgPathStyles(colors.secondary70, 1.5)};
  }

  &:not(.disabled) {
    .categoryDetails {
      width: calc(100% - 28px);
    }
  }

  &.active {
    --category-typeahead-border-color: ${colors.accentBlue};
    background: ${colors.white};

    .categoryDetails {
      width: calc(100% - 28px);
    }
  }

  &.disabled {
    --category-typeahead-border-color: ${colors.transparent};
    --category-typeahead-border-hover-color: ${colors.transparent};

    .selectArrow {
      display: none;
    }
  }
`

interface CategoryProps {
  transaction: EntityTransaction
  editable?: boolean
  onCategorySelected: (category: EntityCategory) => void
  updatedCategory: EntityCategory | undefined
  closedTimestamp: number | undefined | null
  iconOnly?: boolean
}

export const EditableCategoryCell: React.FC<CategoryProps> = ({
  transaction,
  editable,
  onCategorySelected,
  updatedCategory,
  closedTimestamp,
}) => {
  const { allowEdits } = useAllowTransactionsTableEdits()
  const {
    settings: { editLedgerEnabled },
  } = useUserSettings()

  const category = updatedCategory || transaction.displayCategory

  const disabled = React.useMemo(
    () =>
      !editable ||
      !editLedgerEnabled ||
      (isTransaction(transaction) &&
        transaction.sources?.some(({ name }) => name === ProviderType.QuickBooks)) ||
      !allowEdits ||
      (closedTimestamp ? closedTimestamp >= transaction.timestamp : false),
    [editable, editLedgerEnabled, transaction, allowEdits, closedTimestamp]
  )

  if (disabled && category) {
    return (
      <CategoryHover
        category={category}
        triggerContent={
          <StyledCategoryTypeahead
            category={category}
            onSelect={onCategorySelected}
            disabled={disabled}
            tooltipDisplay="never"
            showCategoryHover={false}
          />
        }
      />
    )
  }

  return (
    <StyledCategoryTypeahead
      category={category}
      onSelect={onCategorySelected}
      disabled={disabled}
      className={updatedCategory ? "active" : ""}
      showCategoryHover
    />
  )
}

export const EditableSplitCategoryCell: React.FC<CategoryProps> = ({
  transaction,
  editable,
  onCategorySelected,
  updatedCategory,
  closedTimestamp,
  iconOnly = false,
}) => {
  const { allowEdits } = useAllowTransactionsTableEdits()
  const {
    settings: { editLedgerEnabled },
  } = useUserSettings()

  const category = updatedCategory || transaction.splitCategory

  const disabled = React.useMemo(
    () =>
      !editable ||
      !editLedgerEnabled ||
      !category?.name ||
      (isTransaction(transaction) &&
        transaction.sources?.some(({ name }) => name === ProviderType.QuickBooks)) ||
      !allowEdits ||
      (closedTimestamp ? closedTimestamp >= transaction.timestamp : false),
    [editable, editLedgerEnabled, category?.name, transaction, allowEdits, closedTimestamp]
  )

  if (disabled && category) {
    return (
      <CategoryHover
        category={category}
        triggerContent={
          <StyledCategoryTypeahead
            category={category}
            onSelect={onCategorySelected}
            disabled={disabled}
            iconOnly={iconOnly}
            tooltipDisplay="never"
            showCategoryHover={false}
          />
        }
      />
    )
  }

  return (
    <StyledCategoryTypeahead
      category={category}
      onSelect={onCategorySelected}
      disabled={disabled}
      className={updatedCategory ? "active" : ""}
      iconOnly={iconOnly}
      customTooltip={iconOnly ? category?.name || "Multiple" : undefined}
      tooltipDisplay={iconOnly ? "always" : "overflow"}
      showCategoryHover={!!category}
    />
  )
}
